import { useRef, useState, useEffect, useCallback } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useRecoilState, useRecoilValue } from 'recoil'
import clsx from 'clsx'
import IconChain from '@/components/icons/IconChain'
import IconChevronDown from '@/components/icons/IconChevronDown'
import IconWallet from '@/components/icons/IconWallet'
import Account from '@/components/header/Account'
import Notification from '@/components/header/Notification'
import Setting from '@/components/header/Setting'
import Menu from '@/components/header/Menu'
import { useWalletSelector } from '@/contexts/WalletSelectorContext'
import { isMobileValue } from '@/recoil/Device'
import { networkState, chainIdState } from '@/recoil/Network'
import { marketSymbolState } from '@/recoil/Market'
import useOnClickOutside from '@/hooks/useOnClickOutside'
import { CHAIN_LIST } from '@/constants/chain'

import './Header.css'

const NETWORK_DOMAIN_MAP = {
  mainnet: 'https://dex.btse.com',
  testnet: 'https://dex.btse.io',
  nearMainnet: 'https://dex.btse.co'
}

function formatAccountId(id = '') {
  return id.length > 25 ? `${id.slice(0, 7)}...${id.slice(-7)}` : id
}

export default function Header() {
  const navigate = useNavigate()
  const location = useLocation()

  const { walletReady, setWalletNetwork, modal, accountId } =
    useWalletSelector()
  const [network] = useRecoilState(networkState)
  const [chainId] = useRecoilState(chainIdState)
  const [marketSymbol] = useRecoilState(marketSymbolState)
  const isMobile = useRecoilValue(isMobileValue)
  const [chainListActive, setChainListActive] = useState(false)
  const chainListRef = useRef()
  useOnClickOutside(chainListRef, () => setChainListActive(false))

  const isTradePage = location.pathname.includes('/trade')

  const connectWallet = useCallback(() => {
    if (walletReady) {
      return navigate('/wallet')
    }
    modal.show()
  }, [walletReady, navigate, modal])

  useEffect(() => {
    setWalletNetwork(network)
  }, [network, setWalletNetwork])

  const switchNetwork = useCallback(
    async (network, chainId) => {
      const isTestnet = network === 'testnet'
      const isNearProtocol = String(chainId).includes('near')
      const domain = isNearProtocol
        ? NETWORK_DOMAIN_MAP[chainId]
        : NETWORK_DOMAIN_MAP[network]
      const defaultChainQuery = isNearProtocol ? '' : `/?chainId=${chainId}`
      const path = isNearProtocol ? location.pathname : ''
      window.location.replace(`${domain}${path}${defaultChainQuery}`)
    },
    [location.pathname]
  )

  const MainnetNetworkList = []
  const TestnetNetworkList = []

  const chainName = `Near ${network === 'mainnet' ? 'Mainnet' : 'Testnet'}`

  CHAIN_LIST.concat([
    // { id: 'nearTestnet', name: 'Near', testnet: true },
    { id: 'nearMainnet', name: 'Near Mainnet', testnet: false }
  ]).forEach(({ name, id, testnet }) => {
    const network = testnet ? 'testnet' : 'mainnet'
    const list = testnet ? TestnetNetworkList : MainnetNetworkList

    list.push(
      <div
        key={id}
        className={clsx('dropdown-list-row', {
          active: chainId === id,
          testnet
        })}
        onClick={() => chainId !== id && switchNetwork(network, id)}
      >
        <IconChain id={id} />
        <span className="chain-name">{name}</span>
      </div>
    )
  })

  const walletName = accountId ? formatAccountId(accountId) : 'Connect Wallet'
  const tradePagePath = `/trade/${marketSymbol}`

  return (
    <header className="app-header noselect">
      <div className="header-left-container">
        <NavLink className="logo-link" to={tradePagePath}>
          <img
            className="logo-icon"
            src={
              isMobile ? '/logo192.png' : '/static/img/btse-logo.svg?20230823'
            }
            alt="btse logo"
          />
        </NavLink>
        {!isMobile && (
          <>
            <NavLink className="navigation-link" to={tradePagePath}>
              Trade
            </NavLink>
            <NavLink className="navigation-link" to="/markets">
              Markets
            </NavLink>
          </>
        )}
      </div>

      <div className="header-right-container">
        <div className="dropdown-list-container">
          <div
            ref={chainListRef}
            className={clsx('dropdown-list-button noselect', {
              active: chainListActive
            })}
            onClick={() => setChainListActive(active => !active)}
          >
            <IconChain id={chainId} />
            {!isMobile && <span className="chain-name">{chainName}</span>}
            <IconChevronDown className="icon-chevron-down" />
            <div className="dropdown-list-panel">
              <div className="list-column">
                <div className="list-label">Mainnet</div>
                {MainnetNetworkList}
              </div>
              <div className="list-column">
                <div className="list-label">Testnet</div>
                {TestnetNetworkList}
              </div>
            </div>
          </div>
        </div>

        <div
          className={clsx('wallet', {
            connected: accountId
          })}
          onClick={connectWallet}
        >
          <IconWallet />
          <div className="wallet-name">{walletName}</div>
        </div>
        {walletReady && (
          <>
            {!isMobile && <Account />}
            <Notification />
          </>
        )}
        {isTradePage && <Setting />}
        {isMobile && <Menu />}
      </div>
    </header>
  )
}
